'use client';

import Link from '@eliadmin/core/src/components/link';
import { useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { usePathname } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchServices } from '@eliadmin/core/src/api/services/services-api';
import type { Service } from '../../types/service';
import { IoIosArrowDown } from 'react-icons/io';

export type CategoryMenuProps = {
  className?: string;
};

export function CategoryMenu(props: PropsWithChildren<CategoryMenuProps>) {
  return (
    <ul className={cn('flex flex-row items-center gap-2', 'ea-category-menu', props.className)}>
      {props.children}
    </ul>
  );
}

export type CategoryMenuItemProps = {
  title: string;
  href?: string;
  onClick?: () => void;
  order?: number;
  className?: {
    root?: string;
    link?: string;
  };
};

export function CategoryMenuItem(props: CategoryMenuItemProps) {
  const LinkComp = props.href ? (isRelativeHref(getSafeHref(props.href)) ? Link : 'a') : 'span';
  const pathname = usePathname();
  const [isDropdownVisible, setDropdownVisible] = useState(false);

  const { data: services = [] } = useQuery({
    queryKey: [CacheKey.REFERENCES, 'sk'],
    queryFn: () => fetchServices({ locale: 'sk' }),
  });

  const serviceSlugs = services.map(service => service.slug);
  const isActive =
    pathname !== '/' &&
    ((props.title === 'Služby' && serviceSlugs.some(slug => pathname === `/${slug}`)) ||
      pathname === props.href);

  useEffect(() => {
    // Close dropdown on pathname change
    setDropdownVisible(false);
  }, [pathname]);

  return (
    <li
      style={{ order: props.order }}
      className={cn(
        'relative', // Add relative positioning for the parent
        'flex flex-row',
        'ea-category-menu-item',
        {
          'ea-category-menu-item--active': isActive,
        },
        props.className?.root,
      )}
      onMouseEnter={() => setDropdownVisible(true)}
      onMouseLeave={() => setDropdownVisible(false)}
    >
      <LinkComp {...(LinkComp === Link ? { prefetchOnHover: true } : {})}
        href={props.href ? getSafeHref(props.href) : (undefined as any) ?? '#'}
        className={cn(
          'flex items-center justify-center gap-2 text-sm font-medium',
          'ea-category-menu-item__link',
          props.className?.link,
        )}
        onClick={props.onClick}
      >
        {props.title} {props.title === 'Služby' && <IoIosArrowDown className="text-primary" />}
      </LinkComp>
      {props.title === 'Služby' && isDropdownVisible && (
        <ul className="absolute left-0 top-full w-max flex-col border border-gray-200 bg-white shadow-md z-[60]">
          {services.map(service => (
            <li key={service.slug} className="p-2">
              <Link prefetchOnHover href={`/${service.slug}`}>{service.name}</Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
